import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { useState } from 'react';
import { API_BASE_URL } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface TailParlayResult {
  success: boolean;
  addedLegs: number;
  unavailableLegs: string[];
  error?: string;
}

interface AvailableLeg {
  legId: string;
  player: string | null;
  playerId: number | null;
  team: string;
  opponentTeam: string | null;
  isHomeGame: boolean | null;
  statType: string;
  line: number | null;
  overUnder: 'Over' | 'Under' | null;
  odds: string;
  gameId: string | null;
  gameDate: string | null;
  marketKey: string | null;
  leagueKey: LeagueKey;
  sportsbook: string;
}

// =============================================================================
// useParlayTail Hook
// =============================================================================

export function useParlayTail() {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const addAllLegs = useParlayBuilderStore((state) => state.addAllLegs);

  const tailParlay = async (parlayId: string): Promise<TailParlayResult> => {
    setLoading(true);

    try {
      // Call validation endpoint
      const res = await authFetch(`${API_BASE_URL}/api/leaderboard/validate-tail/${parlayId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const error = 'Failed to validate parlay';
        showToast(error, 'error');
        return {
          success: false,
          addedLegs: 0,
          unavailableLegs: [],
          error,
        };
      }

      const data = await res.json();

      // Check if any legs are unavailable
      if (data.unavailableLegs.length > 0) {
        const reasons = data.unavailableLegs.map(
          (leg: { legId: string; reason: string }) => leg.reason,
        );
        showToast(`Cannot tail parlay: ${reasons.join(', ')}`, 'error');
        return {
          success: false,
          addedLegs: 0,
          unavailableLegs: reasons,
        };
      }

      // Transform available legs to BuilderLeg format
      const builderLegs: BuilderLeg[] = data.availableLegs.map((leg: AvailableLeg) => ({
        id: `tail-${leg.legId}`,
        player: leg.player,
        playerId: leg.playerId,
        team: leg.team,
        opponentTeam: leg.opponentTeam,
        isHomeGame: leg.isHomeGame,
        statType: leg.statType,
        line: leg.line,
        overUnder: leg.overUnder,
        odds: leg.odds,
        gameId: leg.gameId,
        gameDate: leg.gameDate,
        sportsbook: leg.sportsbook,
        marketKey: leg.marketKey,
        source: 'manual' as const,
        provenance: 'manual' as const,
        leagueKey: leg.leagueKey,
      }));

      // Add legs to builder
      const result = addAllLegs(builderLegs);

      if (result.addedCount > 0) {
        showToast(
          `Added ${result.addedCount} leg${result.addedCount > 1 ? 's' : ''} to parlay builder`,
          'success',
        );

        if (result.blockedCount > 0) {
          showToast(
            `${result.blockedCount} leg${result.blockedCount > 1 ? 's' : ''} couldn't be added: ${result.blockedReasons.join(', ')}`,
            'warning',
          );
        }
      } else {
        showToast('No legs could be added to builder', 'error');
      }

      return {
        success: result.addedCount > 0,
        addedLegs: result.addedCount,
        unavailableLegs: result.blockedReasons,
      };
    } catch (err) {
      const error = (err as Error).message;
      showToast(`Failed to tail parlay: ${error}`, 'error');
      return {
        success: false,
        addedLegs: 0,
        unavailableLegs: [],
        error,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    tailParlay,
    loading,
  };
}
